import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Define content block types
interface ContentBlock {
  type: 'paragraph' | 'table' | 'heading' | 'list' | 'heading2' | 'heading3';
  content: string | TableData;
  level?: number; // For headings
}

interface TableData {
  head: string[][];
  body: string[][];
}

export interface ReportSection {
  title: string;
  content: string;
  type: 'text' | 'chart' | 'skills';
  promptName?: string;
  modelUsed?: string;
  personaUsed?: {
    name: string;
    description: string;
  };
}

export interface ReportData {
  id: string;
  title: string;
  created_at: string;
  status: string;
  sections: ReportSection[];
  summary: string;
  aiModels: string[];
  personasUsed: Array<{
    name: string;
    description: string;
  }>;
  metadata?: {
    totalPromptsProcessed: number;
    totalPersonasInvolved: number;
    totalModelsUsed: number;
    processingTimestamp: string;
    sessionId: string;
  };
}

export class PDFGenerator {
  private doc: jsPDF;
  private yPosition: number;
  private pageHeight: number;
  private margin: number;
  private lineHeight: number;

  constructor() {
    this.doc = new jsPDF();
    this.yPosition = 20;
    this.pageHeight = this.doc.internal.pageSize.height;
    this.margin = 20;
    this.lineHeight = 7;
  }

  private checkPageBreak(additionalHeight: number = 20): void {
    if (this.yPosition + additionalHeight > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.yPosition = 20;
    }
  }

  /**
   * Process markdown formatting within text (bold, italic, inline code)
   */
  private processInlineMarkdown(text: string): Array<{text: string, isBold: boolean}> {
    const segments: Array<{text: string, isBold: boolean}> = [];
    let currentText = '';
    let i = 0;

    while (i < text.length) {
      // Check for bold markers (**text**)
      if (text.substr(i, 2) === '**') {
        // Save current text segment
        if (currentText) {
          segments.push({text: currentText, isBold: false});
          currentText = '';
        }

        // Find closing **
        const closeIndex = text.indexOf('**', i + 2);
        if (closeIndex !== -1) {
          const boldText = text.substring(i + 2, closeIndex);
          segments.push({text: boldText, isBold: true});
          i = closeIndex + 2;
          continue;
        }
      }

      currentText += text[i];
      i++;
    }

    if (currentText) {
      segments.push({text: currentText, isBold: false});
    }

    return segments;
  }

  private addText(text: string, fontSize: number = 10, isBold: boolean = false, forceBoldNewLine: boolean = true): void {
    this.doc.setFontSize(fontSize);

    // Process inline markdown formatting
    const segments = this.processInlineMarkdown(text);
    const maxWidth = this.doc.internal.pageSize.width - (this.margin * 2);

    // Build array of styled words with their formatting
    interface StyledWord {
      text: string;
      isBold: boolean;
      startsNewLine?: boolean;
    }
    const styledWords: StyledWord[] = [];

    for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
      const segment = segments[segmentIndex];
      const words = segment.text.split(/(\s+)/); // Keep whitespace

      for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
        const word = words[wordIndex];
        if (word.trim()) {
          styledWords.push({
            text: word,
            isBold: isBold || segment.isBold,
            // Force new line before bold text if it's not at the start
            startsNewLine: forceBoldNewLine && segment.isBold && styledWords.length > 0 && wordIndex === 0
          });
        } else if (word) {
          // Preserve spaces between words
          if (styledWords.length > 0) {
            styledWords[styledWords.length - 1].text += word;
          }
        }
      }
    }

    // Render words line by line with proper styling
    let currentLineWords: StyledWord[] = [];
    let currentLineWidth = 0;

    for (let i = 0; i < styledWords.length; i++) {
      const word = styledWords[i];

      // Force new line for bold headings
      if (word.startsNewLine && currentLineWords.length > 0) {
        this.renderStyledLine(currentLineWords);
        currentLineWords = [];
        currentLineWidth = 0;
        this.yPosition += 2; // Extra space before bold section
      }

      // Measure word width with appropriate font style
      this.doc.setFont('helvetica', word.isBold ? 'bold' : 'normal');
      const wordWidth = this.doc.getTextWidth(word.text + ' ');

      if (currentLineWidth + wordWidth > maxWidth && currentLineWords.length > 0) {
        // Render current line
        this.renderStyledLine(currentLineWords);
        currentLineWords = [word];
        currentLineWidth = wordWidth;
      } else {
        currentLineWords.push(word);
        currentLineWidth += wordWidth;
      }
    }

    // Render remaining words
    if (currentLineWords.length > 0) {
      this.renderStyledLine(currentLineWords);
    }

    // Add extra space after paragraph
    this.yPosition += 2;
  }

  private renderStyledLine(words: Array<{text: string, isBold: boolean}>): void {
    this.checkPageBreak(this.lineHeight + 2);

    let xPosition = this.margin;

    for (const word of words) {
      this.doc.setFont('helvetica', word.isBold ? 'bold' : 'normal');
      this.doc.text(word.text, xPosition, this.yPosition);
      xPosition += this.doc.getTextWidth(word.text) + this.doc.getTextWidth(' ');
    }

    this.yPosition += this.lineHeight;
  }

  private addTitle(title: string): void {
    this.checkPageBreak(20); // Increase space check for titles
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin, this.yPosition);
    this.yPosition += 12; // Add more space after titles
  }

  private addSubtitle(subtitle: string): void {
    this.checkPageBreak(15); // Increase space check for subtitles
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(subtitle, this.margin, this.yPosition);
    this.yPosition += 10; // Add more space after subtitles
  }

  private addSeparator(): void {
    this.checkPageBreak(10);
    const pageWidth = this.doc.internal.pageSize.width;
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(this.margin, this.yPosition, pageWidth - this.margin, this.yPosition);
    this.yPosition += 10;
  }

  /**
   * Parse markdown content into structured content blocks
   * Enhanced version with proper markdown formatting (bold, headers, lists)
   */
  private parseMarkdownContent(markdown: string): ContentBlock[] {
    const contentBlocks: ContentBlock[] = [];
    const lines = markdown.split('\n');
    let currentBlock: string[] = [];
    let inTable = false;
    let tableHead: string[][] = [];
    let tableBody: string[][] = [];
    let isHeaderRow = false;
    
    const processTableRow = (row: string): string[] => {
      // Remove leading and trailing pipes and split by pipes
      const rawCells = row.trim().replace(/^\||\|$/g, '').split('|');
      // Trim each cell
      return rawCells.map(cell => cell.trim());
    };
    
    const flushCurrentBlock = () => {
      if (currentBlock.length > 0) {
        const text = currentBlock.join('\n').trim();
        if (text) {
          contentBlocks.push({
            type: 'paragraph',
            content: text
          });
        }
        currentBlock = [];
      }
    };
    
    const flushCurrentTable = () => {
      if (tableHead.length > 0 || tableBody.length > 0) {
        contentBlocks.push({
          type: 'table',
          content: {
            head: tableHead,
            body: tableBody
          }
        });
        tableHead = [];
        tableBody = [];
        inTable = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Check for markdown headers (##, ###, etc.)
      const headerMatch = trimmedLine.match(/^(#{1,3})\s+(.+)$/);
      if (headerMatch) {
        flushCurrentBlock();
        if (inTable) {
          flushCurrentTable();
        }

        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        contentBlocks.push({
          type: level === 1 ? 'heading' : level === 2 ? 'heading2' : 'heading3',
          content: headerText,
          level: level
        });
        continue;
      }

      // Check for table separator line (e.g., |---|---|)
      const isSeparatorLine = /^\|[\s-:|]*\|$/.test(trimmedLine) && trimmedLine.includes('-');

      // Check for table row (starts and ends with |)
      const isTableRow = trimmedLine.startsWith('|') && trimmedLine.endsWith('|');
      
      if (isTableRow) {
        if (!inTable) {
          // Beginning of a new table
          inTable = true;
          isHeaderRow = true;
          flushCurrentBlock();
        }
        
        if (isHeaderRow && !isSeparatorLine) {
          // This is a header row
          tableHead.push(processTableRow(trimmedLine));
        } else if (isSeparatorLine) {
          // This is a separator row, just mark we're past the header
          isHeaderRow = false;
        } else if (!isHeaderRow && !isSeparatorLine) {
          // This is a body row
          tableBody.push(processTableRow(trimmedLine));
        }
      } else {
        // Not a table row
        if (inTable) {
          // End of table
          flushCurrentTable();
        }
        
        // Regular content
        currentBlock.push(line);
      }
    }
    
    // Flush any remaining content
    if (inTable) {
      flushCurrentTable();
    } else {
      flushCurrentBlock();
    }
    
    return contentBlocks;
  }

  private addReportHeader(report: ReportData): void {
    // Main Title
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(report.title, this.margin, this.yPosition);
    this.yPosition += 15;

    this.addSeparator();
  }

  private addExecutiveSummary(report: ReportData): void {
    this.addTitle('Executive Summary');
    
    // Process summary text into content blocks
    const contentBlocks = this.parseMarkdownContent(report.summary);
    
    for (const block of contentBlocks) {
      if (block.type === 'paragraph') {
        this.addText(block.content as string, 11);
      } else if (block.type === 'table') {
        this.addTable(block.content as TableData);
      }
    }
    
    this.yPosition += 5;
    this.addSeparator();
  }

  private addTableOfContents(report: ReportData): void {
    this.addTitle('Table of Contents');
    
    // Add summary
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Executive Summary', this.margin + 5, this.yPosition);
    this.yPosition += this.lineHeight;

    // Add sections
    report.sections.forEach((section, index) => {
      this.checkPageBreak();
      this.doc.text(`${index + 1}. ${section.title}`, this.margin + 5, this.yPosition);
      this.yPosition += this.lineHeight;
    });

    this.yPosition += 5;
    this.addSeparator();
  }

  /**
   * Calculate intelligent column widths based on content
   */
  private calculateColumnWidths(tableData: TableData): any {
    const columnStyles: any = {};
    const pageWidth = this.doc.internal.pageSize.width - (this.margin * 2);

    if (tableData.head.length === 0) return columnStyles;

    const numColumns = tableData.head[0].length;

    // For tables with many columns, use smaller fixed widths
    if (numColumns >= 6) {
      const colWidth = pageWidth / numColumns;
      for (let i = 0; i < numColumns; i++) {
        columnStyles[i] = {
          cellWidth: colWidth,
          overflow: 'linebreak',
          fontSize: 8 // Smaller font for wide tables
        };
      }
      return columnStyles;
    }

    // For tables with fewer columns, use auto sizing with constraints
    const maxColWidth = pageWidth / numColumns;
    for (let i = 0; i < numColumns; i++) {
      columnStyles[i] = {
        cellWidth: 'auto',
        minCellWidth: Math.min(30, maxColWidth * 0.5),
        maxCellWidth: maxColWidth,
        overflow: 'linebreak'
      };
    }

    return columnStyles;
  }

  private addTable(tableData: TableData): void {
    const estimatedTableHeight = (tableData.head.length * 10) + (tableData.body.length * 8) + 20;
    this.checkPageBreak(estimatedTableHeight);

    // Calculate intelligent column widths
    const columnStyles = this.calculateColumnWidths(tableData);
    const numColumns = tableData.head.length > 0 ? tableData.head[0].length : 0;

    // Keep all pages in portrait orientation for consistency
    // Tables will wrap/scale to fit the portrait page width

    // TypeScript doesn't know about the autoTable method added by the jspdf-autotable plugin
    (this.doc as any).autoTable({
      head: tableData.head,
      body: tableData.body,
      startY: this.yPosition,
      margin: { left: this.margin, right: this.margin },
      styles: {
        fontSize: numColumns > 5 ? 7 : 9,
        cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'left',
        valign: 'top',
        lineColor: [220, 220, 220],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [50, 50, 50],
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [50, 50, 50]
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: columnStyles,
      // Enable row page break for long tables
      rowPageBreak: 'auto',
      showHead: 'everyPage'
    });

    // Update yPosition after the table
    this.yPosition = (this.doc as any).lastAutoTable.finalY + 15;
  }

  private addReportSections(report: ReportData): void {
    report.sections.forEach((section, index) => {
      this.addTitle(`${index + 1}. ${section.title}`);

      // Parse and add content - now handling tables, headers, and formatting properly
      const contentBlocks = this.parseMarkdownContent(section.content);

      for (const block of contentBlocks) {
        if (block.type === 'paragraph') {
          this.addText(block.content as string, 10);
        } else if (block.type === 'table') {
          this.addTable(block.content as TableData);
        } else if (block.type === 'heading') {
          this.addTitle(block.content as string);
        } else if (block.type === 'heading2') {
          this.addSubtitle(block.content as string);
        } else if (block.type === 'heading3') {
          this.checkPageBreak(12);
          this.doc.setFontSize(12);
          this.doc.setFont('helvetica', 'bold');
          this.doc.text(block.content as string, this.margin, this.yPosition);
          this.yPosition += 8;
        }
      }

      this.yPosition += 15; // Add more space between sections
      this.addSeparator();
    });
  }

  private addFooter(report: ReportData): void {
    this.addTitle('Report Information');
    
    if (report.metadata) {
      this.addText(`Processing Session ID: ${report.metadata.sessionId}`, 9);
      this.addText(`Processing Timestamp: ${new Date(report.metadata.processingTimestamp).toLocaleString()}`, 9);
      this.addText(`Total Prompts Processed: ${report.metadata.totalPromptsProcessed}`, 9);
      this.addText(`Total AI Models Used: ${report.metadata.totalModelsUsed}`, 9);
      this.addText(`Total Personas Involved: ${report.metadata.totalPersonasInvolved}`, 9);
    }

    this.yPosition += 10;
    this.addText('This report was generated by FutureLens AI-powered career analysis platform.', 9, true);
    this.addText('For more information, visit: https://inquisitive-daffodil-233759.netlify.app', 9);
  }

  public generatePDF(report: ReportData): jsPDF {
    // Reset position
    this.yPosition = 20;

    // Add report header
    this.addReportHeader(report);

    // Add table of contents
    this.addTableOfContents(report);

    // Add all sections in order
    this.addReportSections(report);

    // Add footer information
    this.addFooter(report);

    return this.doc;
  }

  public downloadPDF(report: ReportData, filename?: string): void {
    const pdf = this.generatePDF(report);
    const defaultFilename = `${report.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename || defaultFilename);
  }
}

// Utility function to generate and download PDF
export const generateReportPDF = (report: ReportData, filename?: string): void => {
  const generator = new PDFGenerator();
  generator.downloadPDF(report, filename);
};